(function () {
  'use strict';

  if (window.__barbellRecommendationThemeTrackerV2) return;
  window.__barbellRecommendationThemeTrackerV2 = true;

  var VERSION = '2.1.3-theme-session-recovery';
  var ENDPOINT = '/apps/barbell/update';
  var SELECTOR = '[data-barbell-rec-track]';
  var VISITOR_COOKIE = '_barbell_visitor';
  var CART_TOKEN_COOKIE = '_barbell_cart_token';
  var CART_TOKEN_MAX_AGE = 60 * 60 * 24 * 30;
  var SESSION_KEY = '_barbell_session_id';
  var SESSION_COOKIE = '_barbell_session';
  var SESSION_COOKIE_MAX_AGE = 30 * 60;
  var VISITOR_COOKIE_MAX_AGE = 2 * 365 * 24 * 60 * 60;
  var PRIMARY_TRACKER_GRACE_MS = 1800;
  var SESSION_LAST_SEEN_KEY = '_barbell_session_last_seen';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  var IMPRESSION_DWELL_MS = 600;
  var PAGE_VIEW_ID = uuid();

  var observer = null;
  var seenImpressions = new WeakSet();
  var seenCartImpressionKeys = new Set(); // one exposure per product per page view
  var impressionTimers = new WeakMap();
  var impressionRetryCounts = new WeakMap();
  var cartToken = getCookie(CART_TOKEN_COOKIE);
  var cartContext = [];
  var cartRefreshPromise = null;
  var lastKnownCart = null;
  var checkoutIdentitySyncPromise = null;
  var checkoutIdentityRetryTimer = null;
  var checkoutIdentityRetryCount = 0;
  var checkoutIdentitySyncedKey = null;
  // The primary storefront app tracker is the sole owner of these shared IDs.
  // Creating them here can prevent it from emitting session_start/unique_visitor.
  var visitorId = null;
  var sessionId = null;
  var pendingCartAdds = new Map();
  var pendingInteractions = [];
  var pendingInteractionTimer = null;
  var debugEnabled = /(?:\?|&)barbell_tracking_debug=1(?:&|$)/.test(window.location.search || '');

  function debug() {
    if (!debugEnabled || !window.console || !console.info) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Barbell recommendation tracker ' + VERSION + ']');
    console.info.apply(console, args);
  }

  function uuid() {
    var cryptoObject = window.crypto || window.msCrypto;
    if (cryptoObject && cryptoObject.randomUUID) return cryptoObject.randomUUID();
    if (cryptoObject && cryptoObject.getRandomValues) {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
        var random = cryptoObject.getRandomValues(new Uint8Array(1))[0] % 16;
        return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
      });
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
      var random = Math.floor(Math.random() * 16);
      return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
    });
  }

  function getCookie(name) {
    var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, maxAge) {
    if (!value) return;
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAge + '; SameSite=Lax; Secure';
  }

  // Read, but NEVER create or refresh, the canonical app tracker's identity.
  // This is important: the primary tracker emits session_start when it first
  // creates the ID. If this recommendation script creates it first, the primary
  // tracker sees an existing session and suppresses its start event.
  function readIdentity() {
    var storedSession = null;
    var lastSeen = 0;
    try {
      storedSession = sessionStorage.getItem(SESSION_KEY);
      lastSeen = Number(sessionStorage.getItem(SESSION_LAST_SEEN_KEY) || 0);
    } catch (error) { /* storage can be blocked */ }
    var sessionIsFresh = lastSeen > 0 && Date.now() - lastSeen < SESSION_TIMEOUT_MS;
    // sessionStorage is canonical for this tab. Cookies are shared across
    // tabs and may contain the ID written by a DIFFERENT storefront session.
    sessionId = sessionIsFresh ? storedSession : null;
    visitorId = getCookie(VISITOR_COOKIE);
  }

  // Primary tracker normally creates the identity and emits lifecycle events.
  // If its app embed is absent, delayed or broken, recommendation interactions
  // must not be silently thrown away. Wait for the embed before taking over.
  function recoverMissingIdentity() {
    readIdentity();
    if (sessionId && visitorId) return false;
    var createdVisitor = false;
    var createdSession = false;
    if (!visitorId) {
      visitorId = uuid();
      setCookie(VISITOR_COOKIE, visitorId, VISITOR_COOKIE_MAX_AGE);
      createdVisitor = true;
    }
    if (!sessionId) {
      var recoveredSession = uuid();
      try {
        sessionStorage.setItem(SESSION_KEY, recoveredSession);
        sessionStorage.setItem(SESSION_LAST_SEEN_KEY, String(Date.now()));
        sessionId = recoveredSession;
        createdSession = true;
      } catch (error) {
        debug('session storage is blocked; refusing to fabricate a cross-tab ID');
        return false;
      }
    }
    setCookie(SESSION_COOKIE, sessionId, SESSION_COOKIE_MAX_AGE);
    // These are valid general analytics event types, sent exactly once when
    // this page *creates* the relevant ID; no duplicate start on page reload.
    if (createdVisitor) sendEvent('unique_visitor', {}, false);
    if (createdSession) sendEvent('session_start', {}, false);
    debug('recovered missing primary tracking identity', { createdVisitor: createdVisitor, createdSession: createdSession });
    refreshCart();
    return true;
  }

  function numberOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function moneyFromMinor(value) {
    var parsed = numberOrNull(value);
    return parsed === null ? null : (parsed / 100).toFixed(2);
  }

  // Theme-side purchase attribution bridge: Shopify's cart attributes can
  // carry the primary app's session/visitor IDs into the order metadata.
  // Important: order ingestion must explicitly read the note_attributes fields;
  // this alone does NOT correct the checkout web pixel or historical orders.
  function checkoutIdentityKey(cart) {
    readIdentity();
    if (!sessionId || !visitorId || !cart || !cart.token) return null;
    if (!Array.isArray(cart.items) || !cart.items.length) return null;
    return String(cart.token) + ':' + sessionId + ':' + visitorId;
  }

  function cartIdentityAlreadySynced(key) {
    if (checkoutIdentitySyncedKey === key) return true;
    try { return sessionStorage.getItem('_barbell_order_identity_synced') === key; }
    catch (error) { return false; }
  }

  function queueCheckoutIdentityRetry() {
    if (checkoutIdentityRetryTimer || checkoutIdentityRetryCount >= 10) return;
    checkoutIdentityRetryCount += 1;
    checkoutIdentityRetryTimer = setTimeout(function () {
      checkoutIdentityRetryTimer = null;
      syncCheckoutIdentity(lastKnownCart);
    }, 600);
  }

  function syncCheckoutIdentity(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;
    var key = checkoutIdentityKey(cart);
    if (!key) {
      // Let the Shopify app embed create the ID and emit session_start first.
      queueCheckoutIdentityRetry();
      return;
    }
    if (cartIdentityAlreadySynced(key) || checkoutIdentitySyncPromise) return;
    checkoutIdentityRetryCount = 0;

    // Double underscores = private cart attributes; buyers do not see these
    // in checkout. No cart line items, quantities or prices are modified.
    var attributes = {
      '__barbell_session_id': sessionId,
      '__barbell_visitor_id': visitorId
    };
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    rootPath = rootPath.replace(/\/?$/, '/');
    checkoutIdentitySyncPromise = fetch(rootPath + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart identity sync failed: ' + response.status);
      checkoutIdentitySyncedKey = key;
      try { sessionStorage.setItem('_barbell_order_identity_synced', key); }
      catch (error) { /* session storage blocked */ }
      debug('checkout identity saved to Shopify cart attributes');
    }).catch(function (error) {
      // Analytics must not block cart rendering or checkout.
      debug('checkout identity not saved', error && error.message);
    }).finally(function () { checkoutIdentitySyncPromise = null; });
  }

  function refreshCart() {
    if (cartRefreshPromise) return cartRefreshPromise;
    cartRefreshPromise = fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to read /cart.js');
        return response.json();
      })
      .then(function (cart) {
        lastKnownCart = cart;
        if (cart && cart.token) {
          cartToken = String(cart.token);
          setCookie(CART_TOKEN_COOKIE, cartToken, CART_TOKEN_MAX_AGE);
        }
        cartContext = Array.isArray(cart && cart.items) ? cart.items.map(function (item) {
          return {
            product_id: item.product_id || null,
            variant_id: item.variant_id || item.id || null,
            product_title: item.product_title || item.title || null,
            product_type: item.product_type || null,
            price_minor: item.final_price == null ? item.price : item.final_price,
            quantity: item.quantity || 0
          };
        }) : [];
        syncCheckoutIdentity(cart);
        return cartToken;
      })
      .catch(function (error) {
        debug('cart identity unavailable', error && error.message);
        return cartToken;
      })
      .finally(function () { cartRefreshPromise = null; });
    return cartRefreshPromise;
  }

  function cartSourceProducts(element) {
    var wrapper = element.closest && element.closest('.drawer__upsell-products');
    var node = wrapper && wrapper.querySelector('[data-barbell-cart-source-products]');
    if (node) {
      try { return JSON.parse(node.textContent || '[]'); } catch (error) {}
    }
    return cartContext;
  }

  function recommendationData(element, interactionType) {
    var formVariant = element.querySelector('form[action*="/cart/add"] [name="id"]');
    var selectedOption = element.querySelector('.drawer__upsell-product-variants option:checked');
    var targetVariantId = formVariant && formVariant.value ? formVariant.value : element.dataset.targetVariantId;
    var targetPriceMinor = selectedOption && selectedOption.dataset.priceMinor ? selectedOption.dataset.priceMinor : element.dataset.targetPriceMinor;
    var sourceProducts = element.dataset.barbellSurface === 'cart_upsell' ? cartSourceProducts(element) : [];

    return {
      recommendation_surface: element.dataset.barbellSurface || null,
      interaction_type: interactionType || null,
      shopify_product_id: numberOrNull(element.dataset.targetProductId),
      shopify_variant_id: numberOrNull(targetVariantId),
      source_product_id: numberOrNull(element.dataset.sourceProductId),
      source_variant_id: numberOrNull(element.dataset.sourceVariantId),
      source_product_title: element.dataset.sourceProductTitle || null,
      source_product_type: element.dataset.sourceProductType || null,
      source_price: moneyFromMinor(element.dataset.sourcePriceMinor),
      source_currency: element.dataset.currency || null,
      target_product_id: numberOrNull(element.dataset.targetProductId),
      target_variant_id: numberOrNull(targetVariantId),
      target_product_title: element.dataset.targetProductTitle || null,
      target_product_type: element.dataset.targetProductType || null,
      target_price: moneyFromMinor(targetPriceMinor),
      target_currency: element.dataset.currency || null,
      metadata: {
        page_view_id: PAGE_VIEW_ID,
        tracker_version: VERSION,
        source_products: sourceProducts
      }
    };
  }

  function buildEvent(eventType, data) {
    readIdentity();
    var event = {
      event_id: uuid(),
      session_id: sessionId,
      visitor_id: visitorId,
      cart_token: cartToken || getCookie(CART_TOKEN_COOKIE),
      event_type: eventType,
      event_subtype: null,
      page_path: window.location.pathname,
      occurred_at: new Date().toISOString(),
      metadata: { page_view_id: PAGE_VIEW_ID, tracker_version: VERSION }
    };
    if (data) event = Object.assign(event, data);
    event.metadata = Object.assign({ page_view_id: PAGE_VIEW_ID, tracker_version: VERSION }, event.metadata || {});
    return event;
  }

  function validRecommendationEvent(event) {
    if (!event || !event.session_id) return false;
    if (event.event_type === 'session_start' || event.event_type === 'unique_visitor') return true;
    return !!(event.recommendation_surface && event.target_product_id);
  }

  function sendEvent(eventType, data, preferBeacon) {
    var event = buildEvent(eventType, data);
    if (!validRecommendationEvent(event)) {
      debug('not sent because required join data is missing', event);
      return false;
    }

    var body = JSON.stringify({ events: [event] });
    debug('sending', event);

    if (preferBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(ENDPOINT, blob)) return true;
      } catch (error) { debug('sendBeacon failed', error && error.message); }
    }

    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (response) {
        debug('response', response.status, event.event_type, event.recommendation_surface);
        if (!response.ok) throw new Error('Tracking endpoint returned HTTP ' + response.status);
      }).catch(function (error) {
        debug('POST failed', error && error.message);
        if (!preferBeacon && navigator.sendBeacon) {
          try { navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' })); } catch (beaconError) {}
        }
      });
      return true;
    } catch (error) {
      debug('fetch unavailable', error && error.message);
      return false;
    }
  }

  // The app embed owns the session ID. A short retry allows the embed to
  // initialize before we discard an interaction; it never creates an ID.
  function sendInteraction(eventType, data, preferBeacon) {
    if (sendEvent(eventType, data, preferBeacon)) return true;
    readIdentity();
    if (sessionId || pendingInteractions.length >= 30) return false;
    pendingInteractions.push({ type: eventType, data: data, beacon: preferBeacon, attempts: 0 });
    if (!pendingInteractionTimer) {
      pendingInteractionTimer = setInterval(function () {
        pendingInteractions = pendingInteractions.filter(function (item) {
          item.attempts += 1;
          if (sendEvent(item.type, item.data, item.beacon)) return false;
          return item.attempts < 15;
        });
        if (!pendingInteractions.length) {
          clearInterval(pendingInteractionTimer);
          pendingInteractionTimer = null;
        }
      }, 250);
    }
    return false;
  }

  function visibleRatio(element) {
    if (!element || !element.isConnected) return 0;
    var style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return 0;
    var rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 0;
    var visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    var visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return (visibleWidth * visibleHeight) / (rect.width * rect.height);
  }

  // A cart drawer commonly replaces its cards during AJAX cart updates. The
  // old WeakSet only remembers DOM nodes and therefore loses deduplication on
  // rerender. Keep a product-level key for the current document/page_view_id.
  function cartImpressionKey(element) {
    if (element.dataset.barbellSurface !== 'cart_upsell') return null;
    var productId = element.dataset.targetProductId;
    return productId ? 'cart_upsell:' + String(productId) : null;
  }

  function scheduleImpression(element) {
    if (seenImpressions.has(element)) return;
    var key = cartImpressionKey(element);
    if (key && seenCartImpressionKeys.has(key)) {
      seenImpressions.add(element);
      return;
    }
    var oldTimer = impressionTimers.get(element);
    if (oldTimer) clearTimeout(oldTimer);
    impressionTimers.set(element, setTimeout(function () {
      if (seenImpressions.has(element) || visibleRatio(element) < 0.5) return;
      // Another copy of the card might already have fired in this page view.
      if (key && seenCartImpressionKeys.has(key)) {
        seenImpressions.add(element);
        return;
      }
      // Do not mark an impression as recorded before it has an app-owned
      // session ID and has actually been queued for delivery.
      var sent = sendEvent('recommendation_impression', recommendationData(element, 'view'), false);
      if (sent) {
        seenImpressions.add(element);
        if (key) seenCartImpressionKeys.add(key);
      } else {
        // If the app embed initializes after the theme, retry briefly rather
        // than inventing our own session ID or retrying forever.
        var attempts = (impressionRetryCounts.get(element) || 0) + 1;
        impressionRetryCounts.set(element, attempts);
        if (attempts < 5) {
          impressionTimers.set(element, setTimeout(function () {
            scheduleImpression(element);
          }, 800));
        }
      }
    }, IMPRESSION_DWELL_MS));
  }

  function createObserver() {
    if (!('IntersectionObserver' in window)) return null;
    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var element = entry.target;
        var oldTimer = impressionTimers.get(element);
        if (oldTimer) clearTimeout(oldTimer);
        if (seenImpressions.has(element)) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) scheduleImpression(element);
      });
    }, { threshold: [0, 0.5, 1] });
  }

  function observeNode(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.matches && node.matches(SELECTOR)) {
      if (observer) observer.observe(node);
      else if (visibleRatio(node) >= 0.5) scheduleImpression(node);
    }
    if (!node.querySelectorAll) return;
    node.querySelectorAll(SELECTOR).forEach(function (element) {
      if (observer) observer.observe(element);
      else if (visibleRatio(element) >= 0.5) scheduleImpression(element);
    });
  }

  function scan() {
    document.querySelectorAll(SELECTOR).forEach(function (element) {
      if (observer) observer.observe(element);
      else if (visibleRatio(element) >= 0.5) scheduleImpression(element);
    });
  }

  function onClick(event) {
    if (!event.target || !event.target.closest) return;
    var recommendation = event.target.closest(SELECTOR);
    if (!recommendation) return;
    var actionable = event.target.closest('a[href], button, input[type="submit"], input[type="button"]');
    if (!actionable || !recommendation.contains(actionable) || actionable.disabled) return;
    var addForm = actionable.closest('form[action*="/cart/add"]');
    sendInteraction('recommendation_click', recommendationData(recommendation, addForm ? 'add_to_cart' : 'navigate'), !!event.target.closest('a[href]'));
  }

  // The native Shopify product-form.js publishes PUB_SUB_EVENTS.cartUpdate
  // only after its /cart/add.js request succeeds. Never count a form submit
  // itself as a successful recommendation ATC.
  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    var recommendation = form.closest(SELECTOR);
    if (!recommendation) return;
    var variantInput = form.querySelector('[name="id"]');
    var variantId = variantInput && variantInput.value;
    if (!variantId) return;
    pendingCartAdds.set(String(variantId), {
      recordedAt: Date.now(),
      data: recommendationData(recommendation, 'submit')
    });
  }

  function onSuccessfulCartUpdate(detail) {
    if (!detail || detail.source !== 'product-form' || !detail.productVariantId) return;
    var variantId = String(detail.productVariantId);
    var pending = pendingCartAdds.get(variantId);
    if (!pending) return; // Ignore ordinary product-page Add to Cart operations.
    pendingCartAdds.delete(variantId); // Ignore duplicate cart-update notifications.
    if (Date.now() - pending.recordedAt > 15000) return;
    sendInteraction('recommendation_add_to_cart', pending.data, false);
    setTimeout(refreshCart, 350);
  }

  function onFailedCartUpdate(detail) {
    if (detail && detail.source === 'product-form' && detail.productVariantId) {
      pendingCartAdds.delete(String(detail.productVariantId));
    }
  }

  function onVariantChange(event) {
    if (!event.target || !event.target.matches || !event.target.matches('.drawer__upsell-product-variants')) return;
    var recommendation = event.target.closest(SELECTOR);
    var option = event.target.selectedOptions && event.target.selectedOptions[0];
    if (!recommendation || !option) return;
    recommendation.dataset.targetVariantId = option.value || recommendation.dataset.targetVariantId;
    recommendation.dataset.targetPriceMinor = option.dataset.priceMinor || recommendation.dataset.targetPriceMinor;
  }

  function boot() {
    observer = createObserver();
    // A failed/missing app embed must not cause all events to disappear.
    setTimeout(recoverMissingIdentity, PRIMARY_TRACKER_GRACE_MS);
    refreshCart();
    scan();

    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('change', onVariantChange, true);
    // Uses the theme's EXISTING pub/sub success signal. No changes to cart,
    // checkout, pricing, order fulfillment or product-form.js are needed.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onSuccessfulCartUpdate);
      subscribe(PUB_SUB_EVENTS.cartError, onFailedCartUpdate);
    }

    if ('MutationObserver' in window && document.body) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(observeNode);
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener('pageshow', function () {
      readIdentity();
      refreshCart();
      scan();
    });

    window.BarbellRecommendationThemeTracker = {
      version: VERSION,
      rescan: scan,
      status: function () {
        readIdentity();
        return {
          version: VERSION,
          markers: document.querySelectorAll(SELECTOR).length,
          session_id: sessionId,
          visitor_id: visitorId,
          cart_token: cartToken || getCookie(CART_TOKEN_COOKIE),
          checkout_identity_synced: !!checkoutIdentitySyncedKey
        };
      }
    };
    debug('booted', window.BarbellRecommendationThemeTracker.status());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
